import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const Frame161: React.FC = () => {
  const [selectedView, setSelectedView] = useState('overview');
  const [selectedPeriod, setSelectedPeriod] = useState('all');

  const collectionStats = {
    totalItems: 47,
    totalValue: 89750,
    averageValue: 1909,
    authenticated: 42,
    pendingAuthentication: 3,
    wishlistItems: 12,
    recentlyAdded: 5
  };

  const collectionItems = [
    {
      id: 1,
      name: 'Victorian Diamond Brooch',
      category: 'Jewelry',
      period: 'Victorian',
      year: 1885,
      value: 2299,
      condition: 'Excellent',
      authenticated: true,
      image: '/api/placeholder/150/150',
      addedDate: '2024-12-01',
      location: 'Display Case A',
      insurance: 'Covered',
      notes: 'Family heirloom from grandmother'
    },
    {
      id: 2,
      name: 'Art Deco Emerald Ring',
      category: 'Jewelry',
      period: 'Art Deco',
      year: 1925,
      value: 4599,
      condition: 'Very Good',
      authenticated: true,
      image: '/api/placeholder/150/150',
      addedDate: '2024-11-28',
      location: 'Safe Deposit Box',
      insurance: 'Covered',
      notes: 'Purchased at estate auction'
    },
    {
      id: 3,
      name: 'Georgian Silver Tea Set',
      category: 'Silver',
      period: 'Georgian',
      year: 1820,
      value: 3899,
      condition: 'Good',
      authenticated: true,
      image: '/api/placeholder/150/150',
      addedDate: '2024-11-25',
      location: 'Dining Room Cabinet',
      insurance: 'Pending',
      notes: 'Needs minor restoration'
    },
    {
      id: 4,
      name: 'Edwardian Mahogany Desk',
      category: 'Furniture',
      period: 'Edwardian',
      year: 1905,
      value: 1899,
      condition: 'Excellent',
      authenticated: false,
      image: '/api/placeholder/150/150',
      addedDate: '2024-12-10',
      location: 'Home Office',
      insurance: 'Not Covered',
      notes: 'Authentication scheduled for next week'
    },
    {
      id: 5,
      name: 'Renaissance Oil Painting',
      category: 'Art',
      period: 'Renaissance',
      year: 1650,
      value: 15999,
      condition: 'Fair',
      authenticated: true,
      image: '/api/placeholder/150/150',
      addedDate: '2024-10-15',
      location: 'Living Room Wall',
      insurance: 'Covered',
      notes: 'Requires climate-controlled environment'
    },
    {
      id: 6,
      name: 'Victorian Pocket Watch',
      category: 'Timepieces',
      period: 'Victorian',
      year: 1892,
      value: 2899,
      condition: 'Excellent',
      authenticated: true,
      image: '/api/placeholder/150/150',
      addedDate: '2024-12-05',
      location: 'Display Case B',
      insurance: 'Covered',
      notes: 'Recently serviced movement'
    }
  ];

  const recentActivity = [
    {
      id: 1,
      type: 'added',
      item: 'Edwardian Mahogany Desk',
      date: '2024-12-10',
      details: 'Added to collection, authentication pending'
    },
    {
      id: 2,
      type: 'authenticated',
      item: 'Victorian Pocket Watch',
      date: '2024-12-05',
      details: 'Authentication completed by Dr. Margaret Chen (99.8% confidence)'
    },
    {
      id: 3,
      type: 'valued',
      item: 'Art Deco Emerald Ring',
      date: '2024-11-30',
      details: 'Professional appraisal updated value to $4,599'
    },
    {
      id: 4,
      type: 'insured',
      item: 'Victorian Diamond Brooch',
      date: '2024-11-28',
      details: 'Insurance coverage updated and confirmed'
    },
    {
      id: 5,
      type: 'maintenance',
      item: 'Renaissance Oil Painting',
      date: '2024-11-20',
      details: 'Condition assessment completed, requires climate control'
    }
  ];

  const upcomingTasks = [
    {
      id: 1,
      task: 'Schedule authentication for Edwardian Mahogany Desk',
      priority: 'High',
      dueDate: '2024-12-15',
      category: 'Authentication'
    },
    {
      id: 2,
      task: 'Update insurance for Georgian Silver Tea Set',
      priority: 'Medium',
      dueDate: '2024-12-20',
      category: 'Insurance'
    },
    {
      id: 3,
      task: 'Annual condition review for all items',
      priority: 'Low',
      dueDate: '2024-12-31',
      category: 'Maintenance'
    },
    {
      id: 4,
      task: 'Organize professional photography session',
      priority: 'Medium',
      dueDate: '2024-12-25',
      category: 'Documentation'
    }
  ];

  const valueByCategory = [
    { category: 'Art', value: 15999, count: 1, percentage: 17.8 },
    { category: 'Jewelry', value: 6898, count: 2, percentage: 7.7 },
    { category: 'Silver', value: 3899, count: 1, percentage: 4.3 },
    { category: 'Timepieces', value: 2899, count: 1, percentage: 3.2 },
    { category: 'Furniture', value: 1899, count: 1, percentage: 2.1 }
  ];

  const filteredItems = selectedPeriod === 'all' 
    ? collectionItems 
    : collectionItems.filter(item => item.period === selectedPeriod);

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'added': return '➕';
      case 'authenticated': return '✅';
      case 'valued': return '💰';
      case 'insured': return '🛡️';
      case 'maintenance': return '🔧';
      default: return '📝';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High': return 'bg-red-100 text-red-800';
      case 'Medium': return 'bg-yellow-100 text-yellow-800';
      case 'Low': return 'bg-green-100 text-green-800';
      default: return 'bg-grey-100 text-grey-800';
    }
  };

  return (
    <div className="min-h-screen bg-grey-50">
      {/* Header */}
      <header className="bg-white border-b border-grey-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/frame-160" className="text-blue-600 hover:text-blue-700 mr-4">
                ← Back to Authentication
              </Link>
              <h1 className="text-xl font-display font-semibold text-grey-900">My Collection</h1>
            </div>
            <div className="flex items-center space-x-4">
              <button className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">
                Add Item
              </button>
              <button className="bg-grey-100 text-grey-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-grey-200 transition-colors">
                Export Report
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* View Toggle */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex space-x-1 bg-grey-100 rounded-lg p-1">
            {[
              { id: 'overview', name: 'Overview', icon: '📊' },
              { id: 'items', name: 'Items', icon: '📦' },
              { id: 'analytics', name: 'Analytics', icon: '📈' }
            ].map((view) => (
              <button
                key={view.id}
                onClick={() => setSelectedView(view.id)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  selectedView === view.id
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-grey-600 hover:text-grey-900'
                }`}
              >
                <span className="mr-2">{view.icon}</span>
                {view.name}
              </button>
            ))}
          </div>

          {selectedView === 'items' && (
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="border border-grey-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Periods</option>
              <option value="Victorian">Victorian</option>
              <option value="Edwardian">Edwardian</option>
              <option value="Georgian">Georgian</option>
              <option value="Art Deco">Art Deco</option>
              <option value="Renaissance">Renaissance</option>
            </select>
          )}
        </div>

        {/* Overview View */}
        {selectedView === 'overview' && (
          <div className="space-y-8">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-lg border border-grey-200 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-grey-600">Total Items</p>
                    <p className="text-2xl font-bold text-grey-900">{collectionStats.totalItems}</p>
                  </div>
                  <span className="text-3xl">📦</span>
                </div>
              </div>
              
              <div className="bg-white rounded-lg border border-grey-200 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-grey-600">Total Value</p>
                    <p className="text-2xl font-bold text-grey-900">${collectionStats.totalValue.toLocaleString()}</p>
                  </div>
                  <span className="text-3xl">💰</span>
                </div>
              </div>
              
              <div className="bg-white rounded-lg border border-grey-200 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-grey-600">Authenticated</p>
                    <p className="text-2xl font-bold text-green-600">{collectionStats.authenticated}</p>
                  </div>
                  <span className="text-3xl">✅</span>
                </div>
              </div>
              
              <div className="bg-white rounded-lg border border-grey-200 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-grey-600">Avg. Value</p>
                    <p className="text-2xl font-bold text-grey-900">${collectionStats.averageValue.toLocaleString()}</p>
                  </div>
                  <span className="text-3xl">📊</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Recent Activity */}
              <div className="bg-white rounded-lg border border-grey-200 p-6">
                <h3 className="text-lg font-display font-semibold text-grey-900 mb-6">Recent Activity</h3>
                <div className="space-y-4">
                  {recentActivity.map((activity) => (
                    <div key={activity.id} className="flex items-start space-x-3">
                      <span className="text-lg">{getActivityIcon(activity.type)}</span>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-grey-900">{activity.item}</p>
                        <p className="text-xs text-grey-600">{activity.details}</p>
                        <p className="text-xs text-grey-500 mt-1">{activity.date}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Upcoming Tasks */}
              <div className="bg-white rounded-lg border border-grey-200 p-6">
                <h3 className="text-lg font-display font-semibold text-grey-900 mb-6">Upcoming Tasks</h3>
                <div className="space-y-4">
                  {upcomingTasks.map((task) => (
                    <div key={task.id} className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-grey-900">{task.task}</p>
                        <p className="text-xs text-grey-600">{task.category} • Due {task.dueDate}</p>
                      </div>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(task.priority)}`}>
                        {task.priority}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Items View */}
        {selectedView === 'items' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-display font-semibold text-grey-900">
                Collection Items ({filteredItems.length})
              </h3>
              <div className="flex space-x-2">
                <button className="text-sm text-grey-600 hover:text-grey-900">Grid</button>
                <button className="text-sm text-blue-600">List</button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredItems.map((item) => (
                <div key={item.id} className="bg-white rounded-lg border border-grey-200 overflow-hidden hover:shadow-md transition-shadow">
                  <img src={item.image} alt={item.name} className="w-full h-48 object-cover" />
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-medium text-grey-900">{item.name}</h4>
                      {item.authenticated ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                          ✓ Auth
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
                          Pending
                        </span>
                      )}
                    </div>
                    
                    <div className="space-y-1 text-sm text-grey-600 mb-3">
                      <p>{item.category} • {item.period} ({item.year})</p>
                      <p>Condition: {item.condition}</p>
                      <p>Location: {item.location}</p>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-bold text-grey-900">${item.value.toLocaleString()}</span>
                      <div className="flex space-x-2">
                        <button className="text-xs text-blue-600 hover:text-blue-700">View</button>
                        <button className="text-xs text-grey-600 hover:text-grey-700">Edit</button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Analytics View */}
        {selectedView === 'analytics' && (
          <div className="space-y-8">
            <div className="bg-white rounded-lg border border-grey-200 p-6">
              <h3 className="text-lg font-display font-semibold text-grey-900 mb-6">Value by Category</h3>
              <div className="space-y-4">
                {valueByCategory.map((category) => (
                  <div key={category.category} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-4 h-4 bg-blue-500 rounded"></div>
                      <span className="font-medium text-grey-900">{category.category}</span>
                      <span className="text-sm text-grey-600">({category.count} items)</span>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-grey-900">${category.value.toLocaleString()}</p>
                      <p className="text-sm text-grey-600">{category.percentage}%</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white rounded-lg border border-grey-200 p-6">
                <h3 className="text-lg font-display font-semibold text-grey-900 mb-6">Authentication Status</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-grey-700">Authenticated</span>
                    <span className="font-medium text-green-600">{collectionStats.authenticated} items</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-grey-700">Pending</span>
                    <span className="font-medium text-yellow-600">{collectionStats.pendingAuthentication} items</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-grey-700">Not Required</span>
                    <span className="font-medium text-grey-600">2 items</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-grey-200 p-6">
                <h3 className="text-lg font-display font-semibold text-grey-900 mb-6">Insurance Coverage</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-grey-700">Fully Covered</span>
                    <span className="font-medium text-green-600">38 items</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-grey-700">Pending</span>
                    <span className="font-medium text-yellow-600">6 items</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-grey-700">Not Covered</span>
                    <span className="font-medium text-red-600">3 items</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Frame161; 